import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef } from 'react';
import { useTransitionController } from 'util/react-transition-component';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import Text from '../../atoms/text/Text';
import { AnimatedFigures } from './AnimatedFigures';
import type { StatisticsProps, StatisticsRef } from './Statistics.data';
import styles from './Statistics.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './Statistics.transitions';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';

export default function Statistics({
  isVisible,
  className,
  blok,
  ref,
}: StatisticsProps & { ref?: Ref<StatisticsRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useThemeScrollBlock(elementRef, blok.use_accent_color);
  useChangeTransitionState(transitionController, isVisible);

  return (
    <div ref={elementRef} className={classNames(styles.container, className)} {...sbEditable(blok)}>
      <div
        ref={elementRef}
        className={classNames(styles.statistics, className, {
          [styles.fewStats]: blok.values.length <= 2,
        })}
        {...sbEditable(blok)}
      >
        {blok.values.map(
          (stat) =>
            (stat.number || stat.number === 0) && (
              <div className={styles.stat} key={stat._uid}>
                <AnimatedFigures stat={stat} />
                <Text
                  as="p"
                  size="body-standard"
                  disableTriggerInOnScroll={false}
                  transitionInType="slideUpFadeIn"
                  className={styles.description}
                >
                  {stat.description}
                </Text>
              </div>
            ),
        )}
      </div>
    </div>
  );
}
